// The shop: category, size-in-stock and colour filters, sort, the view (fisheye slider, grid or small grid) and quick
// add, all in the page. Each card carries its data (type, colour, sizes in stock, price, date, photo), so nothing
// reloads. The choice is kept in the URL (?type=Hoodie&size=M,L&color=black&sort=price-low), so a filtered shop can be
// linked to, for example from the menu; the view is remembered in this browser ('9ine-grid').
// Ported from the approved concept (concept/src/08-shop.js).
import { $, $$, root, store, session, motion, setText, decode, scramble, depixelate, dissolve, onPageIn } from 'fx';
import { open, close } from 'layers';
import { addToCart } from 'cart';
import { createSlider, hexRGB } from 'slider';

const shop = $('[data-shop]');
if (shop) init();

function init() {
  const grid = $('#pgrid');
  const cards = $$('.pcard', grid);
  const side = $('#filters');
  const strings = JSON.parse(($('#shop-strings') || {}).textContent || '{}');
  const titleEl = $('[data-shop-title]');
  const SORTS = ['featured', 'newest', 'price-low', 'price-high'];
  const F = { type: '', sizes: new Set(), colors: new Set(), sort: 'featured' };
  const list = (v) => (v || '').split(',').map((s) => s.trim()).filter(Boolean);
  const toggle = (set, v) => { if (set.has(v)) set.delete(v); else set.add(v); };

  // Start from the URL.
  const q = new URLSearchParams(location.search);
  F.type = q.get('type') || '';
  if (F.type && !cards.some((c) => c.dataset.type === F.type)) F.type = '';
  list(q.get('size')).forEach((s) => F.sizes.add(s));
  list(q.get('color')).forEach((c) => F.colors.add(c.toLowerCase()));
  if (SORTS.includes(q.get('sort'))) F.sort = q.get('sort');

  // skip: leave one filter out, to count what each choice in that group would show
  const passes = (c, skip) =>
    (skip === 'type' || !F.type || c.dataset.type === F.type) &&
    (skip === 'color' || !F.colors.size || F.colors.has(c.dataset.color)) &&
    (skip === 'size' || !F.sizes.size || [...F.sizes].some((s) => c.dataset.sizes.includes(`|${s}|`)));
  const byPos = (a, b) => a.dataset.pos - b.dataset.pos;
  const ORDER = {
    featured: byPos,
    newest: (a, b) => b.dataset.created - a.dataset.created || byPos(a, b),
    'price-low': (a, b) => a.dataset.price - b.dataset.price || byPos(a, b),
    'price-high': (a, b) => b.dataset.price - a.dataset.price || byPos(a, b)
  };

  let lastTitle = titleEl.textContent.trim();
  let band = null; // the slider, made the first time the slider view is used
  let listNow = []; // the cards shown, in order, as places in `cards`
  function apply(first) {
    const shown = cards.filter((c) => passes(c));
    const sorted = [...cards].sort(ORDER[F.sort]);
    sorted.forEach((c) => grid.append(c));
    cards.forEach((c) => { c.hidden = !shown.includes(c); });
    listNow = sorted.filter((c) => shown.includes(c)).map((c) => cards.indexOf(c));
    if (band) band.setList(listNow);
    const n = shown.length;
    $('#grid-empty').hidden = n > 0;
    $('[data-shop-count]').textContent = `(${n})`;

    // Where you are: breadcrumb and the big title follow the category.
    const cat = side && $$('[data-fcat]', side).find((b) => b.dataset.fcat === F.type);
    const label = F.type ? (cat ? cat.dataset.label : F.type) : titleEl.dataset.all;
    $('[data-shop-crumb]').textContent = `${shop.dataset.label} / ${label}`;
    if (label !== lastTitle) {
      lastTitle = label;
      setText(titleEl, label);
      decode(titleEl, { step: 26, hold: 160 });
    }

    if (side) {
      $$('[data-fcat]', side).forEach((b) => {
        const t = b.dataset.fcat;
        const count = cards.filter((c) => passes(c, 'type') && (!t || c.dataset.type === t)).length;
        $('[data-cat-count]', b).textContent = `(${count})`;
        b.setAttribute('aria-checked', String(t === F.type));
      });
      $$('.fchip[data-size]', side).forEach((b) => b.setAttribute('aria-pressed', String(F.sizes.has(b.dataset.size))));
      $$('.fchip[data-color]', side).forEach((b) => {
        b.setAttribute('aria-pressed', String(F.colors.has(b.dataset.color)));
        const count = cards.filter((c) => passes(c, 'color') && c.dataset.color === b.dataset.color).length;
        $('[data-color-count]', b).textContent = `(${count})`;
      });
      const showBtn = $('[data-filter-show]', side);
      $('[data-show-label]', side).textContent = n === 1 ? strings.showOne : n ? strings.show.replace('[n]', n) : strings.none;
      showBtn.disabled = !n;
    }
    const active = F.sizes.size + F.colors.size + (F.type ? 1 : 0);
    $$('[data-filter-count]').forEach((el) => { el.textContent = active ? `(${active})` : ''; });
    const sortOpt = $(`#sort-drop .opt[data-sort="${F.sort}"]`);
    $$('#sort-drop .opt').forEach((b) => b.setAttribute('aria-checked', String(b === sortOpt)));
    if (sortOpt) $('[data-sort-val]').textContent = sortOpt.textContent.trim();

    if (!first) {
      const url = new URL(location.href);
      ['type', 'size', 'color', 'sort'].forEach((k) => url.searchParams.delete(k));
      if (F.type) url.searchParams.set('type', F.type);
      if (F.sizes.size) url.searchParams.set('size', [...F.sizes].join(','));
      if (F.colors.size) url.searchParams.set('color', [...F.colors].join(','));
      if (F.sort !== 'featured') url.searchParams.set('sort', F.sort);
      history.replaceState(history.state, '', url);
    }
  }

  /* filter panel */
  if (side) {
    $$('[data-filter-open]').forEach((b) => b.addEventListener('click', () => {
      open(side, { scrim: true, onClose: () => b.setAttribute('aria-expanded', 'false') });
      b.setAttribute('aria-expanded', 'true');
      dissolve(side);
      decode($('#filters-title'), { delay: 120, step: 26, hold: 160 });
      $$('.label', side).forEach((l, k) => decode(l, { delay: 260 + k * 110, step: 18 }));
    }));
    $$('[data-fcat]', side).forEach((b) => b.addEventListener('click', () => { F.type = b.dataset.fcat; apply(); }));
    $$('.fchip[data-size]', side).forEach((b) => b.addEventListener('click', () => { toggle(F.sizes, b.dataset.size); apply(); }));
    $$('.fchip[data-color]', side).forEach((b) => b.addEventListener('click', () => { toggle(F.colors, b.dataset.color); apply(); }));
    $('[data-filter-clear]', side).addEventListener('click', () => { F.type = ''; F.sizes.clear(); F.colors.clear(); apply(); });
    $('[data-filter-show]', side).addEventListener('click', () => close(side));
  } else {
    $$('[data-filter-open]').forEach((b) => { b.hidden = true; });
  }

  /* sort: a dropdown that pushes the grid down */
  const sortBtn = $('#sort-btn');
  const sortDrop = $('#sort-drop');
  const setSortDrop = (on) => {
    sortDrop.classList.toggle('open', on);
    sortBtn.setAttribute('aria-expanded', String(on));
    if (on) $$('.opt', sortDrop).forEach((o, k) => decode(o, { delay: 140 + k * 60, step: 16, hold: 120 }));
  };
  sortBtn.addEventListener('click', () => setSortDrop(sortBtn.getAttribute('aria-expanded') !== 'true'));
  $$('.opt', sortDrop).forEach((b) => b.addEventListener('click', () => {
    F.sort = b.dataset.sort;
    apply();
    setSortDrop(false);
    sortBtn.focus();
  }));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sortBtn.getAttribute('aria-expanded') === 'true') { setSortDrop(false); sortBtn.focus(); }
  });

  /* quick add under the product in the middle of the slider: the card's own size buttons */
  const quickRow = $('#quick-row');
  const quickSizes = $('#quick-sizes');
  const quickNote = $('#quick-note');
  let quickFor = -1;
  const hideQuick = () => quickRow.classList.remove('on');
  function showQuick(i) {
    if (mode !== 'slider' || i < 0) return;
    if (quickFor !== i) {
      quickFor = i;
      const card = cards[i];
      const src = $('.pcard-quick', card);
      quickSizes.textContent = '';
      if (src) $$('button, a', src).forEach((b) => { const q = b.cloneNode(true); q.className = 'qsize'; quickSizes.append(q); });
      quickRow.setAttribute('aria-label', src ? src.getAttribute('aria-label') : $('.pcard-name a', card).textContent.trim());
      const stock = $('.pcard-stock', card);
      setText(quickNote, src ? (stock ? stock.textContent.trim() : '') : strings.soldOut);
    }
    quickRow.classList.add('on');
  }
  quickSizes.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-quick]');
    if (!b || b.disabled) return;
    const fd = new FormData();
    fd.append('id', b.dataset.quick);
    fd.append('quantity', '1');
    b.disabled = true;
    try {
      await addToCart(fd, { open: false });
      setText(quickNote, b.dataset.size ? strings.added.replace('[size]', b.dataset.size) : strings.addedOne);
    } catch (err) {
      setText(quickNote, err.message || '');
    } finally {
      b.disabled = false;
    }
    decode(quickNote, { step: 18, hold: 110 });
    scramble(b, { step: 20, hold: 200 });
  });

  /* the slider: a fisheye band of photos with the names on a ruler under it */
  const stage = $('#shop-stage');
  function openCard(i) {
    const card = cards[i];
    const a = card && $('.pcard-name a', card);
    if (!a) return;
    session.set('9ine-shop-at', card.dataset.handle);
    a.click(); // a real link click, so the iris plays
  }
  function makeBand() {
    if (band) return;
    const photo = hexRGB(getComputedStyle(root).getPropertyValue('--photo'), [0.94, 0.94, 0.94]);
    band = createSlider({
      el: $('#shop-slider'),
      strip: $('#shop-strip'),
      live: $('#shop-live'),
      scope: stage,
      lens: true,
      stage: true,
      quick: true,
      keys: true,
      labels: { drag: strings.drag, view: strings.view, position: strings.position },
      data: cards.map((c) => ({ img: c.dataset.img, name: $('.pcard-name a', c).textContent.trim(), meta: c.dataset.meta, opaque: true, bg: photo, zoom: 1.2, parallax: true })),
      isActive: () => mode === 'slider',
      onOpen: (i) => openCard(i),
      onMove: () => hideQuick(),
      onSettle: (i) => showQuick(i),
      onEnterKey: () => { const i = band.active(); if (i >= 0) openCard(i); }
    });
    band.setList(listNow);
    // The band changes size when the sort dropdown opens, not only when the window does.
    if ('ResizeObserver' in window) new ResizeObserver(() => { if (mode === 'slider') { band.measure(); band.render(); } }).observe(stage);
  }

  /* view: slider, grid or small grid (FLIP between the grids so cards glide to their new places) */
  const MODES = ['slider', 'm', 's'];
  let mode = MODES.includes(store.get('9ine-grid')) ? store.get('9ine-grid') : 'm';
  function setMode(m, animate) {
    if (!MODES.includes(m)) return;
    const was = mode;
    const visible = cards.filter((c) => !c.hidden);
    const before = animate && motion && was !== 'slider' && m !== 'slider' ? visible.map((c) => c.getBoundingClientRect()) : null;
    mode = m;
    store.set('9ine-grid', m);
    root.classList.toggle('grid-s', m === 's');
    root.classList.toggle('shop-slider', m === 'slider');
    $$('[data-density]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.density === m)));
    hideQuick();
    quickFor = -1;
    if (m === 'slider') {
      makeBand();
      if (animate) { window.scrollTo(0, 0); band.enter(); }
      return;
    }
    if (was === 'slider' && animate) window.scrollTo(0, 0);
    if (!before) return;
    visible.forEach((c, i) => {
      const a = before[i];
      const b = c.getBoundingClientRect();
      if (!b.width) return;
      const dx = a.left - b.left;
      const dy = a.top - b.top;
      const s = a.width / b.width;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(s - 1) < 0.01) return;
      c.animate([{ transformOrigin: '0 0', transform: `translate(${dx}px, ${dy}px) scale(${s})` }, { transformOrigin: '0 0', transform: 'none' }], { duration: 650, easing: 'cubic-bezier(.4, 0, .15, 1)' });
    });
  }
  $$('[data-density]').forEach((b) => b.addEventListener('click', () => { if (b.dataset.density !== mode) setMode(b.dataset.density, true); }));

  /* Opening a product keeps the list as shown (filters and sort included) for the visit, so Previous / Next and
     "Back to shop" on the product page follow it (product.js). The cards link straight to /products/…. */
  grid.addEventListener('click', (e) => {
    if (!e.target.closest('a[href]')) return;
    const items = listNow.map((i) => cards[i].dataset.handle).filter(Boolean);
    session.set('9ine-shop-list', JSON.stringify({ url: location.pathname + location.search, items }));
  });

  /* quick add on the cards */
  $$('.pcard-quick > span').forEach((s) => { s.dataset.text = s.textContent.trim(); });
  grid.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-quick]');
    if (!b || b.disabled) return;
    const label = $('span', b.closest('.pcard-quick'));
    const fd = new FormData();
    fd.append('id', b.dataset.quick);
    fd.append('quantity', '1');
    b.disabled = true;
    try {
      await addToCart(fd, { open: false });
      setText(label, b.dataset.size ? strings.added.replace('[size]', b.dataset.size) : strings.addedOne);
    } catch (err) {
      setText(label, err.message || label.dataset.text);
    } finally {
      b.disabled = false;
    }
    decode(label, { step: 18, hold: 110 });
    clearTimeout(label._reset);
    label._reset = setTimeout(() => setText(label, label.dataset.text), 1800);
  });

  /* photos load in as pixels and names decode as cards scroll into view */
  if (motion && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        io.unobserve(e.target);
        depixelate($('img', e.target));
        decode($('.pcard-name a', e.target), { delay: 80, step: 14, hold: 110 });
      }
    }, { rootMargin: '0px 0px -6% 0px' });
    cards.forEach((c) => io.observe(c));
  }
  cards.forEach((c) => c.addEventListener('mouseenter', () => scramble($('.pcard-name a', c), { step: 14, hold: 200 })));

  apply(true);
  setMode(mode, false);
  if (mode === 'slider') {
    // Coming back from a product opened in the slider: that product is in the middle again.
    const back = session.get('9ine-shop-at');
    const i = back ? cards.findIndex((c) => c.dataset.handle === back) : -1;
    if (i >= 0) band.jumpTo(i);
    onPageIn(() => { if (mode === 'slider') band.enter(); });
  }
  session.remove('9ine-shop-at');
}
